import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {View} from 'react-native';
import {Divider} from 'react-native-paper';

import sy from '~/styles';
import {
  Text,
  PrimaryButton,
  ServiceRequestStatusIcon,
  Pressable,
  Dialog,
  InputChatFloating,
} from '~/components/controls';
import {service_request as serviceRequestApi} from '~/api/private';
import {STATUS_CODES} from '~/types/statuses';
import {
  canInvoice,
  isNotReadyForInvoicing,
  isRobRequestReady,
  isArchived,
} from '~/types/rob';
import Format from '~/lib/format';
import {Tabs} from '~/types/tabs';

import ErrorIcon from '~/images/md-icons/error/materialicons/24px.svg';

const renderStatusIcon = (status) => (
  <ServiceRequestStatusIcon
    status={status}
    fill="#FFFFFF"
    iconProps={{width: 18, height: 18}}
  />
);

const Footer = ({
  damage_id,
  editable,
  status,
  overview,
  robContract,
  invoice,
  amount,
  robErrors,
  onDoRequest,
  onRefresh,
  onInvoice,
  onShowRobRequestErrors,
  onChangeTab,
}) => {
  const [pendingMessage, setPendingMessage] = useState(null);

  const [showSaveMessageDialog, setShowSaveMessageDialog] = useState(false);

  const onSendChatMessage = async () => {
    const {success} = await serviceRequestApi.send_message(
      damage_id,
      pendingMessage,
    );

    if (success) {
      setPendingMessage(null);
      onRefresh();
    }
  };

  const onSendChatAttachment = async (attachment) => {
    const {success} = await serviceRequestApi.send_attachment(
      damage_id,
      attachment,
    );

    if (success) {
      onRefresh();
    }
  };

  const changeStatus = async (change) => {
    const {success} = await serviceRequestApi.change_status(damage_id, change);
    if (success) {
      onRefresh();
    }
  };

  let open_tasks = overview.components
    .flatMap((c) => c.tasks ?? [])
    ?.filter((task) => task.is_main_task)
    .some((task) => !overview.rob?.components[task.id]);

  let submitButton = <></>;
  if (
    isArchived(overview?.rob?.status) ||
    overview?.status === STATUS_CODES.Cancelled
  ) {
    // Left intentionally empty
  } else if (canInvoice(status) && !open_tasks) {
    submitButton = (
      <PrimaryButton
        onPress={() => changeStatus(STATUS_CODES.Done)}
        disabled={isNotReadyForInvoicing(overview?.rob?.invoicing?.status)}
        icon={renderStatusIcon(STATUS_CODES.Done)}>
        GEREED MELDEN
      </PrimaryButton>
    );
  } else if (status === STATUS_CODES.Reserved) {
    submitButton = (
      <PrimaryButton
        onPress={() => changeStatus(STATUS_CODES.CheckIn)}
        icon={renderStatusIcon(STATUS_CODES.CheckIn)}>
        INCHECKEN
      </PrimaryButton>
    );
  } else if (
    status === STATUS_CODES.CheckIn ||
    status === STATUS_CODES.Modified ||
    status === STATUS_CODES.Denied ||
    open_tasks
  ) {
    let ready = isRobRequestReady(
      robContract,
      overview.components,
      overview.rob,
      false,
      false,
    );

    if (
      overview.components?.length === 0 &&
      overview.status === STATUS_CODES.Modified &&
      !ready
    ) {
      ready = true;
    }

    submitButton = (
      <PrimaryButton
        disabled={!ready}
        onPress={async () => {
          if (pendingMessage?.length > 0) {
            setShowSaveMessageDialog(true);
            return true;
          }

          await onDoRequest();
        }}
        icon={renderStatusIcon(STATUS_CODES.ApprovalRequested)}>
        VOORSTELLEN
      </PrimaryButton>
    );
  } else if (status === STATUS_CODES.ApprovalRequested) {
    submitButton = (
      <PrimaryButton
        disabled={true}
        icon={renderStatusIcon(STATUS_CODES.ApprovalRequested)}>
        VOORSTELLEN
      </PrimaryButton>
    );
  } else if ([STATUS_CODES.Done, STATUS_CODES.DoneNoInvoice].includes(status)) {
    submitButton = (
      <PrimaryButton
        disabled={
          (!overview?.invoice_number && !invoice?.bahnkick_can_invoice) ||
          overview.rob?.invoicing?.status !== 'ReadyForInvoicing'
        }
        onPress={onInvoice}
        icon={renderStatusIcon(STATUS_CODES.Invoiced)}>
        FACTUREREN
      </PrimaryButton>
    );
  }

  const rob_request_errors = robErrors?.filter((item) => !item.task_id);

  return (
    status !== STATUS_CODES.Invoiced && (
      <>
        {editable && (
          <>
            <InputChatFloating
              is_rob={true}
              message={pendingMessage}
              onChange={setPendingMessage}
              onSend={onSendChatMessage}
              onAttachment={onSendChatAttachment}
              onViewSentMessage={() => onChangeTab(Tabs.Messages)}
            />

            <Dialog
              title="Bericht opslaan?"
              visible={showSaveMessageDialog}
              buttons={[
                {
                  text: 'Annuleren',
                  onPress: () => setShowSaveMessageDialog(false),
                },
                {
                  text: 'Opslaan',
                  onPress: async () => {
                    await onSendChatMessage();
                    setShowSaveMessageDialog(false);
                  },
                },
              ]}>
              <View style={[sy['py-4']]}>
                <Text>Het getypte bericht is nog niet opgeslagen.</Text>
              </View>
            </Dialog>
          </>
        )}

        <Divider style={sy['bg-gray88']} />
        <View
          style={[
            sy['bg-white'],
            sy['flex-row'],
            sy['justify-between'],
            sy['items-center'],
            sy['py-3'],
            sy['px-4'],
          ]}>
          {canInvoice(status) ? (
            <View></View>
          ) : (
            <Text style={sy.mediumPlus}>{Format.price(amount)}</Text>
          )}
          <View style={[sy['flex-row'], sy['items-center']]}>
            {rob_request_errors?.length > 0 && (
              <Pressable style={[sy['pr-4']]} onPress={onShowRobRequestErrors}>
                <ErrorIcon fill="#972727" />
              </Pressable>
            )}
            {submitButton}
          </View>
        </View>
      </>
    )
  );
};

Footer.propTypes = {
  damage_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  editable: PropTypes.bool,
  status: PropTypes.string,
  overview: PropTypes.object,
  robContract: PropTypes.object,
  invoice: PropTypes.object,
  amount: PropTypes.number,
  robErrors: PropTypes.array,
  onDoRequest: PropTypes.func,
  onRefresh: PropTypes.func,
  onInvoice: PropTypes.func,
  onShowRobRequestErrors: PropTypes.func,
  onChangeTab: PropTypes.func,
};

export default Footer;
